/**
 * Maps soccer 1X2 outcomes (home / tie / away) to event_extra_data teams.
 *
 * Outcome titles from the API do not always match home_team / away_team strings.
 * Resolution order:
 *   1. Identify tie by known title (Tie, Draw, …)
 *   2. Match home or away on the two team outcomes (exact → short code → contains)
 *   3. If one team matches, the other team outcome gets the remaining slot
 *   4. Fallback: token overlap between outcome title and team name
 */

const TIE_TITLES = new Set(['tie', 'draw', 'empate', 'x', 'draw/tie'])

const normalize = value =>
  (value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')

const tokenize = value =>
  normalize(value)
    .split(/[\s\-_/]+/)
    .filter(token => token.length > 1)

/** Tie outcome title is stable across markets (1X2). */
export const isTieOutcome = title => TIE_TITLES.has(normalize(title))

/**
 * Whether an outcome title refers to a team from event_extra_data.
 * Tries full name, short code, then partial contains.
 */
const matchesTeam = (outcomeTitle, teamName, shortCode) => {
  const outcome = normalize(outcomeTitle)
  const team = normalize(teamName)
  const code = normalize(shortCode)

  if (!outcome || !team) return false
  if (outcome === team) return true
  if (code && outcome === code) return true
  if (outcome.includes(team) || team.includes(outcome)) return true
  if (code && (outcome.includes(code) || code.includes(outcome))) return true

  return false
}

/** Shared-word count between outcome title and team name (last-resort matcher). */
const overlapScore = (outcomeTitle, teamName) => {
  const outcomeTokens = new Set(tokenize(outcomeTitle))
  const teamTokens = tokenize(teamName)

  if (!outcomeTokens.size || !teamTokens.length) return 0

  return teamTokens.filter(token => outcomeTokens.has(token)).length
}

const pickHomeAwayByOverlap = (teamOutcomes, sportsData) => {
  if (teamOutcomes.length !== 2) return { home: null, away: null }

  const [outcomeA, outcomeB] = teamOutcomes
  const aHomeScore = overlapScore(outcomeA.title, sportsData?.home_team)
  const aAwayScore = overlapScore(outcomeA.title, sportsData?.away_team)

  if (aHomeScore > aAwayScore) {
    return { home: outcomeA, away: outcomeB }
  }
  if (aAwayScore > aHomeScore) {
    return { home: outcomeB, away: outcomeA }
  }

  return { home: null, away: null }
}

/**
 * @returns {{ home: Object|null, tie: Object|null, away: Object|null }}
 */
export const resolveSportOutcomeSlots = (outcomes, sportsData) => {
  const slots = { home: null, tie: null, away: null }
  if (!outcomes?.length) return slots

  slots.tie = outcomes.find(o => isTieOutcome(o.title)) ?? null

  const teamOutcomes = outcomes.filter(o => o.id !== slots.tie?.id)
  let home = null
  let away = null

  // Prefer matching home first; the other team outcome becomes away.
  home = teamOutcomes.find(o =>
    matchesTeam(o.title, sportsData?.home_team, sportsData?.home_team_short_code)
  )
  if (home) {
    away = teamOutcomes.find(o => o.id !== home.id) ?? null
  } else {
    // No home match — try away; the remaining team outcome becomes home.
    away = teamOutcomes.find(o =>
      matchesTeam(o.title, sportsData?.away_team, sportsData?.away_team_short_code)
    )
    if (away) {
      home = teamOutcomes.find(o => o.id !== away.id) ?? null
    }
  }

  // Neither team matched by name — compare shared words in titles.
  if (!home && !away) {
    const overlap = pickHomeAwayByOverlap(teamOutcomes, sportsData)
    home = overlap.home
    away = overlap.away
  }

  slots.home = home
  slots.away = away

  return slots
}

/** Home → tie → away, then any outcomes that could not be classified. */
export const buildOrderedSportOutcomes = (slots, outcomes) => {
  const tie = slots.tie

  if (tie && outcomes.length === 3) {
    const others = outcomes.filter(o => o.id !== tie.id)
    const home = slots.home ?? others[0] ?? null
    const away =
      slots.away ?? others.find(o => o.id !== home?.id) ?? others[1] ?? null

    return [home, tie, away]
  }

  const ordered = [slots.home, slots.tie, slots.away].filter(Boolean)

  if (ordered.length === outcomes.length) return ordered

  const usedIds = new Set(ordered.map(o => o.id))
  return [...ordered, ...outcomes.filter(o => !usedIds.has(o.id))]
}

export const orderSportOutcomes = (outcomes, sportsData) =>
  buildOrderedSportOutcomes(resolveSportOutcomeSlots(outcomes, sportsData), outcomes)

/** Button label: short codes for home/away, full title for tie. */
export const getSportOutcomeLabel = (outcome, slots, sportsData) => {
  if (slots.home?.id === outcome.id) {
    return sportsData?.home_team_short_code ?? outcome.title
  }
  if (slots.away?.id === outcome.id) {
    return sportsData?.away_team_short_code ?? outcome.title
  }
  return outcome.title
}
